import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Table, Badge, Spinner, Input } from "reactstrap";
import PageTitle from "../../../components/Common/PageTitle";
import UserService from '../../../Api/Services/UserService';
import Select from 'react-select';
import Paginations from "common/Paginations";

const UserVerification = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [npiLoading, setNpiLoading] = useState({}); // { [userId]: boolean }
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [totalData, setTotalData] = useState(0);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        console.log('Fetching users for page:', currentPage);
        const response = await UserService.GetUserList(currentPage, {});
        const data = await response.json();
        console.log('API Response:', data);
        
        if (data.status && data.data) {
          console.log('Setting users:', data.data);
          console.log('Pagination meta:', data.meta);
          setUsers(Array.isArray(data.data) ? data.data : []);
          setCurrentPage(data.meta.current_page);
          setTotalPage(data.meta.last_page);
          setTotalData(data.meta.total);
        } else {
          console.error('API returned false status:', data.message);
          setError(data.message || "Failed to fetch users");
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.message || "Error fetching users");
      } finally {
        setLoading(false);
      }
    };

    const fetchDoctors = async () => {
      try {
        const res = await UserService.getDoctors();
        
        if (res.status && Array.isArray(res.data)) {
          setDoctors(res.data);
        } else {
          console.error('Invalid doctors data structure');
          setError(prev => prev + " | Failed to fetch doctors");
        }
      } catch (err) {
        console.error('Error fetching doctors');
        setError(prev => prev + " | Error fetching doctors");
      }
    };

    fetchUsers();
    fetchDoctors();
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAssignDoctor = async (userId, selectedOptions) => {
    try {
      setNpiLoading(prev => ({ ...prev, [userId]: true }));
      
      const npiArray = selectedOptions ? selectedOptions.map(option => option.value) : null;
      
      const res = await UserService.updateUserNpi(userId, npiArray);
      
      if (res.status) {
        setUsers(prev => {
          const newUsers = prev.map(u => {
            if (u.id === userId) {
              return { ...u, npi_id: npiArray || [] };
            }
            return u;
          });
          return newUsers;
        });
      } else {
        throw new Error(res.message || 'Failed to update doctor assignment');
      }
    } catch (error) {
      console.error('Error assigning doctor');
    } finally {
      setNpiLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Custom styles for react-select to allow menu to overflow container
  const selectStyles = {
    menu: (base) => ({ 
      ...base, 
      zIndex: 9999, 
      position: 'absolute' 
    }),
    menuPortal: (base) => ({ 
      ...base, 
      zIndex: 9999 
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e2e8f0',
      borderRadius: '4px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#1a202c',
      padding: '2px 6px'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#4a5568',
      ':hover': {
        backgroundColor: '#cbd5e0',
        color: '#1a202c'
      }
    })
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="container-fluid">
          <PageTitle title="User Verification" />
          <Row>
            <Col lg={12}>
              <Card className="commonboxform">
                <CardBody>
                  <div style={{ padding: '2rem' }}>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <thead>
                          <tr className="text-center">
                            <th>No</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Assigned Doctor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr className="text-center">
                              <td colSpan="4"><Spinner size="sm" /> Loading...</td>
                            </tr>
                          ) : error ? (
                            <tr className="text-center">
                              <td colSpan="4">{error}</td>
                            </tr>
                          ) : users.length === 0 ? (
                            <tr className="text-center">
                              <td colSpan="4">No users found.</td>
                            </tr>
                          ) : (
                            users.map((user, idx) => (
                              <tr key={user.id || idx} className="text-center">
                                <td>{idx + 1}</td>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>
                                  <Select
                                    isMulti
                                    value={user.npi_id ? UserService.logUserNpiMapping(user, doctors) : []}
                                    options={doctors.map(doc => ({
                                      value: doc.npi_id,
                                      label: `${doc.name} (${doc.npi_id})${doc.address ? ' - ' + doc.address : ''}${doc.city ? ', ' + doc.city : ''}`
                                    }))}
                                    onChange={(selected) => handleAssignDoctor(user.id, selected)}
                                    isClearable
                                    isDisabled={npiLoading[user.id]}
                                    placeholder="Assign Doctor"
                                    styles={{
                                      ...selectStyles,
                                      container: (base) => ({
                                        ...base,
                                        width: '100%',
                                        minWidth: '200px'
                                      }),
                                      control: (base) => ({
                                        ...base,
                                        minHeight: '38px'
                                      })
                                    }}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    closeMenuOnSelect={false}
                                    blurInputOnSelect={false}
                                  />
                                  {npiLoading[user.id] && <Spinner size="sm" className="ms-2" />}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                    {users.length > 0 && !loading && !error && (
                      <Paginations
                        currentPage={currentPage}
                        totalPages={totalPage}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default UserVerification; 